import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Card } from '@components/Card';
import { Loader } from '@components/Loader';
import { Logo } from '@components/Logo';
import { MainContent } from '@components/MainContent';
import { OnboardingCompany, OnboardingCompanyAddress } from '@components/Onboarding/Company';
import { IInvoicingOnboardingForm, IPersonalOnboardingForm } from '@components/Onboarding/fields';
import {
  OnboardingInvoicingAddress,
  OnboardingInvoicingContactInfo,
  OnboardingInvoicingIndiginous,
} from '@components/Onboarding/Invoicing';
import { OnboardingPersonal } from '@components/Onboarding/Personal';
import { Stepper } from '@components/Stepper';
import { useAuth } from '@hooks/useAuth';
import { useVendor } from '@hooks/Vendor';
import { useOnboardPersonal, useOnboardInvoicing } from '@hooks/Vendor/useOnboarding';
import { UserOnboardingState } from '@lib/firebase';
import { vendorRoutes } from '@routes/vendor';
import { Color } from '@theme/palette';

// Render first step on load
// Allows easier testing of each step without going through entire flow
const ACTIVE_STEP = 0;

/**
 * Returns onboarding page
 */
export const Onboarding = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Define typed forms
  const personalForm = useForm<IPersonalOnboardingForm>({ mode: 'onSubmit' });
  const invoicingForm = useForm<IInvoicingOnboardingForm>({ mode: 'onSubmit' });

  // Hooks for submitting information
  const { submit: submitPersonalInfo } = useOnboardPersonal();
  const { submit: submitInvoicingInfo } = useOnboardInvoicing();

  // Load vendor from firestore if already exists
  const { data: vendor, isLoading } = useVendor();

  // If already completed certain steps
  const hasCompletedStep1 = vendor?.onboarding_state === UserOnboardingState.FinishedStep1;
  const hasCompletedStep2 = vendor?.onboarding_state === UserOnboardingState.FinishedStep2;

  // Skips first step when already completed step 1 in the past.
  // No need for useState as we're awaiting for vendor to load
  const activeStep = hasCompletedStep1 ? 1 : ACTIVE_STEP;

  // If already completed step 1, prefill personal form (step 1)
  useEffect(() => {
    if (hasCompletedStep1) {
      personalForm.reset(vendor);
    }
  }, [vendor]);

  // If already onboarded step 2 on loading page, skip to home
  if (hasCompletedStep2) {
    navigate(vendorRoutes.home.path);
  }

  if (!currentUser) {
    navigate(vendorRoutes.login.path);
  }

  // After all steps are completed, navigate to home
  const onFinish = () => {
    navigate(vendorRoutes.home.path);
  };

  /**
   * Steps definition for onboarding.
   * Includes which components to render when.
   */
  const steps = [
    {
      label: 'Personal',
      form: {
        useForm: personalForm,
        onSubmit: submitPersonalInfo,
      },
      component: (
        <Stack direction="column" gap={3} sx={{ marginTop: 1 }}>
          <OnboardingPersonal />
          <OnboardingCompany />
          <OnboardingCompanyAddress showAddressAutocomplete={!hasCompletedStep1} />
        </Stack>
      ),
    },
    {
      label: 'Invoicing',
      form: {
        useForm: invoicingForm,
        onSubmit: submitInvoicingInfo,
      },
      component: (
        <Stack direction="column" gap={3} sx={{ marginTop: 1 }}>
          <OnboardingInvoicingAddress />
          <OnboardingInvoicingContactInfo />
          <OnboardingInvoicingIndiginous />
        </Stack>
      ),
    },
  ];

  return (
    <MainContent backgroundColor={Color.Gray4}>
      <Card sx={{ maxWidth: 1000, margin: '0 auto' }}>
        <Stack gap={2} alignItems="center">
          <Logo />
          <Typography variant="h3">Tell us about your service</Typography>

          {isLoading ? (
            <Loader />
          ) : (
            <Stepper activeStep={activeStep} steps={steps} onFinish={onFinish} />
          )}
        </Stack>
      </Card>
    </MainContent>
  );
};
